import Configuration from '@/agilis/util/Configuration'
import FileUploaderSingleElement from '../FileUploader/FileUploaderSingleElement.vue'
import L from 'lodash'
import { mapActions, mapGetters } from 'vuex'

const FILE_UPLOAD_DRAG_OVER_CLASSES = ' file-upload--dragging'

export default {
  name: 'FileUploaderMixin',

  props: {
    propTitle: String,
    trackerDTO: Object
  },
  components: {
    FileUploaderSingleElement
  },
  data () {
    return {
      fileArray: [],
      keyReset: 0,
      keyResetTemplate: 1000
    }
  },

  computed: {
    ...mapGetters({
      userId: 'userDataStore/userId',
      selectedContext: 'contextStore/selectedContext',
      applicationId: 'contextStore/applicationId',
      trackerTypeId: 'contextStore/trackerTypeId',
      trackerType: 'contextStore/trackerType',
      trackerId: 'contextStore/trackerId',
      progress: 'loaderStore/progress',
      validationConfiguration: 'configurationStore/uploadValidation'
    }),

    userCanAddAttachment () {
      return Configuration.auth.userCanAccessFeature(
        this.selectedContext, 'attachment', 'post', this.trackerDTO, this.userId)
    },

    userCanAddTemplate () {
      const privId = `/${this.selectedContext.applicationId}`
        .concat(`/workspace/${this.selectedContext.workspaceId}`)
        .concat(`/tracker/${this.selectedContext.trackerTypeId}/attachmentTemplate?op=post`)

      return this.userCanAddAttachment &&
        Configuration.auth.userHasPrivilege(privId)
    },

    userCanRemoveAttachment () {
      return Configuration.auth.userCanAccessFeature(
        this.selectedContext, 'attachment', 'delete', this.trackerDTO, this.userId)
    },

    title () {
      return this.propTitle
    },

    accept () {
      return this.validationRules.accepted
    },

    acceptTemplate () {
      return this.validationRules.acceptedTemplate
    },

    validationRules () {
      return {
        accepted: this.validationConfiguration.accept || [],
        acceptedTemplate: this.validationConfiguration.acceptTemplate || [],
        attachmentMaxSize: this.validationConfiguration.attachmentMaxSize || 50,
        attachmentMaxNameLength: this.validationConfiguration.attachmentattachmentMaxNameLength || 255
      }
    },

    headers () {
      return Configuration.auth.getHeaders(this.trackerType.attachmentTable, this.selectedContext)
    },

    formRows () {
      return this.trackerType.attachmentTable?.formRows || []
    },

    formField () {
      const columns = L.cloneDeep(this.trackerType.attachmentTable?.columns || {})
      Object.values(columns)
        .filter((c) => c.rules?.excluded)
        .forEach(c => {
          const fieldId = c.id
          const filteredItems = this.items.filter(item => item.id !== this.itemDTO.id)
          const excludedValues = filteredItems.map(item => item[fieldId])
          columns[fieldId].rules.excluded = excludedValues
        })
      return columns
    }
  },

  methods: {
    ...mapActions({
      'notifyError': 'notificationStore/notifyError',
      'notifyUserConfirm': 'notificationStore/notifyUserConfirm',
      'startLoader': 'loaderStore/startLoader',
      'stopLoader': 'loaderStore/stopLoader',
      'startProgress': 'loaderStore/startProgress',
      'updateProgress': 'loaderStore/updateProgress',
      'stopProgress': 'loaderStore/stopProgress'
    }),

    chooseFiles () {
      this.$refs.fileUpload.click()
    },

    chooseTemplate () {
      this.$refs.templateUpload.click()
    },

    // ////////////////////////////////////////////////////////////////
    // Drag & Drop
    // ////////////////////////////////////////////////////////////////
    onDragEnter (e) {
      if (this.userCanAddAttachment) {
        e.preventDefault()

        const container = this.$refs.outerContainer
        if (container.className.indexOf(FILE_UPLOAD_DRAG_OVER_CLASSES) === -1) {
          container.className += FILE_UPLOAD_DRAG_OVER_CLASSES
        }
      }
    },

    onDragLeave () {
      const container = this.$refs.outerContainer
      container.className = container.className.replace(FILE_UPLOAD_DRAG_OVER_CLASSES, '')
    },

    onDragOver (e) {
      this.onDragEnter(e)
    },

    onDrop (event) {
      if (this.userCanAddAttachment) {
        event.preventDefault()
        const files = event.dataTransfer.files
        if (files && files.length > 0) {
          this.onFilePicked({
            target: {
              files: event.dataTransfer.files
            }
          })
        }
        this.onDragLeave()
      }
    },
    // ////////////////////////////////////////////////////////////////

    // ////////////////////////////////////////////////////////////////
    // File Validation
    // ////////////////////////////////////////////////////////////////
    validateFile ({ name, type, size }) {
      const validations = [
        this.validateFileName(name),
        this.validateFileType(type),
        this.validateFileSize(size)
      ]

      return validations.find(v => v.valid === false) || validations[0]
    },

    validateTemplateFile ({ name, type, size }) {
      const validations = [
        this.validateFileName(name),
        this.validateTemplateType(type),
        this.validateFileSize(size)
      ]

      return validations.find(v => v.valid === false) || validations[0]
    },

    validateFileName (name) {
      const maxFileNameLength = this.validationRules.attachmentMaxNameLength

      const traversalRegExp = /\.\.[\\/]/ // substrings to lead to directory traversal attacks (../, ..\)
      // Not validating name for xss: vue will escape the HTML on render unless we use v-html

      const test = !traversalRegExp.test(name) && name.length <= maxFileNameLength
      return {
        valid: test,
        errorMessage: !test ? this.$t('label.fileUploadValidationName') : null
      }
    },

    validateFileType (type) {
      const typeFamily = type.split('/')[0] + '/*' // image/png => image/*
      const test = this.accept.indexOf(type) > -1 ||
      this.accept.indexOf(typeFamily) > -1
      return {
        valid: test,
        errorMessage: !test ? this.$t('label.fileUploadValidationFormat', { format: type }) : null
      }
    },

    validateTemplateType (type) {
      const test = this.acceptTemplate.indexOf(type) > -1
      return {
        valid: test,
        errorMessage: !test ? this.$t('label.fileUploadValidationFormat', { format: type }) : null
      }
    },

    validateFileSize (sizeInBytes) {
      const maxSizeInMB = this.validationRules.attachmentMaxSize

      const sizeInMegaBytes = Math.round((sizeInBytes / 1048576))
      const test = sizeInMegaBytes < maxSizeInMB
      return {
        valid: test,
        errorMessage: !test ? this.$t('label.fileUploadValidationSize', { max: maxSizeInMB }) : null
      }
    },
    // ////////////////////////////////////////////////////////////////

    userCanPreviewAttachment (attachment) {
      const type = attachment.mimeType || ''
      const typeFamily = type.split('/')[0] + '/*' // image/png => image/*

      return type === 'application/pdf' || typeFamily === 'image/*'
    }
  }
}
